import { Link } from 'react-router-dom'
import { ArrowLeft, Home, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import Navbar from './Navbar'
import Footer from './Footer'
import FeatureSwipeWrapper from './FeatureSwipeWrapper'
import FeatureProgressDots from './FeatureProgressDots'
import FeatureNavButtons from './FeatureNavButtons'

interface FeatureLayoutProps {
  title: string
  subtitle: string
  gradientFrom: string
  gradientTo: string
  icon: React.ReactNode
  children: React.ReactNode
  featureName: string
}

export default function FeatureLayout({
  title,
  subtitle,
  gradientFrom,
  gradientTo,
  icon,
  children,
  featureName,
}: FeatureLayoutProps) {
  return (
    <div className="min-h-screen bg-black text-gray-200">
      <Navbar />

      {/* Feature Navigation Buttons */}
      <FeatureNavButtons />

      {/* Feature Progress Dots */}
      <FeatureProgressDots />

      <FeatureSwipeWrapper>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient orbs */}
        <div className="absolute inset-0">
          <div
            className={`absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-full blur-3xl opacity-20`}
          />
          <div
            className={`absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl ${gradientFrom} ${gradientTo} rounded-full blur-3xl opacity-20`}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Breadcrumbs */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm mb-8 text-gray-400"
          >
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <Home className="h-4 w-4" />
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">{featureName}</span>
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.6, delay: 0.2 }}
                className={`w-20 h-20 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl flex items-center justify-center border-2 border-white/20`}
              >
                {icon}
              </motion.div>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">{title}</h1>
            <p className="text-xl text-gray-300 leading-relaxed">{subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <main className="relative">
        <div className="max-w-7xl mx-auto px-6 py-16">{children}</div>
      </main>

      {/* Back to Home CTA */}
      <section className="relative py-16 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
            >
              <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </section>
      </FeatureSwipeWrapper>

      <Footer />
    </div>
  )
}
